import { useRef, useState } from 'react';

import {
  Box,
  IconButton,
  InputAdornment,
  InputProps as InputPropsType,
  createStyles,
  makeStyles,
} from '@material-ui/core';
import { KeyboardDateTimePicker } from '@material-ui/pickers';

import { RiCalendarEventLine as EventRoundedIcon } from '@remixicon/react';

import { ChipInput } from './ChipInput';

type ChipTimestampPickerProps = {
  value?: string | null;
  onChange: (value: string) => void;
  id?: string;
  maxWidth?: number;
  format?: string;
  displayFormat?: string;
  readOnly?: boolean;
  disabled?: boolean;
  InputProps?: Partial<InputPropsType>;
};

const useStyles = makeStyles(() =>
  createStyles({
    button: {
      height: 24,
      width: 24,
      backgroundColor: 'transparent',
    },
    input: {
      maxWidth: (props: { maxWidth: number }) => props.maxWidth,
    },
  }),
);

// Determine if we should display 12- or 24-hour time
const locale = navigator.language;
const ampm = new Intl.DateTimeFormat(locale, {
  hour: 'numeric',
}).resolvedOptions().hour12;

export const ChipTimestampPicker = ({
  value,
  disabled,
  readOnly,
  onChange,
  InputProps,
  id = 'timestamp-picker',
  maxWidth = 80,
  format = "yyyy-MM-dd'T'HH:mm:ss",
  displayFormat = format,
}: ChipTimestampPickerProps) => {
  const classes = useStyles({ maxWidth });
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement | undefined>(undefined);

  const handleChange = (date: Date | null) => {
    try {
      const payload = date ? new Date(date).toISOString() : null;
      if (payload) {
        onChange(payload);
      }
    } catch {
      // Invalid date - ignore
    }
  };

  return (
    <KeyboardDateTimePicker
      inputRef={inputRef}
      variant="inline"
      open={isOpen}
      ampm={ampm}
      autoOk
      emptyLabel="-"
      value={value || null}
      format={displayFormat}
      onClose={() => setIsOpen(false)}
      onChange={handleChange}
      PopoverProps={{
        id: `${id}-picker-popover`,
        // MUI calls anchorEl pre-mount and tolerates the momentary null
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        anchorEl: () => inputRef.current!,
      }}
      TextFieldComponent={props => (
        <Box>
          <ChipInput
            {...props}
            inputRef={inputRef}
            disabled={disabled}
            inputProps={{
              style: {
                maxWidth: maxWidth,
              },
            }}
            required
            InputProps={{
              readOnly: readOnly,
              ...(!(disabled || readOnly) && {
                endAdornment: (
                  <>
                    <InputAdornment position="end">
                      <IconButton
                        className={classes.button}
                        onClick={() => setIsOpen(o => !o)}
                        name={`${id}-picker-icon`}
                        color="inherit"
                      >
                        <EventRoundedIcon size="0.82em" />
                      </IconButton>
                    </InputAdornment>
                    {InputProps?.endAdornment}
                  </>
                ),
              }),
            }}
          />
        </Box>
      )}
    />
  );
};
